/**
 * "Rotate your phone" hint for the landscape backgammon table. The board is auto-rotated 90° to fill a
 * portrait-held phone (PixiApp Case A), so this hint is the ONE thing that must stay upright in
 * physical-screen space — hence it's a DOM overlay, not a Pixi node (a Pixi child of `world` would
 * inherit the 90° rotation and read sideways).
 *
 * Detection caveat: when the Telegram webview is orientation-LOCKED to portrait (the common case the
 * auto-rotate design targets), `innerWidth/innerHeight` and `matchMedia('(orientation)')` keep
 * reporting portrait even after the user physically turns the phone — the webview never re-orients. So
 * we can't reliably detect "now held landscape" there. The hint is therefore guidance, never a trap:
 * it auto-dismisses on a short timer and on tap, and ALSO hides immediately if the viewport genuinely
 * flips to landscape (the not-locked case). Mount it in the scene's onEnter and tear it down via
 * track() so it never leaks across scene swaps.
 */

const AUTO_DISMISS_MS = 4200;

let el: HTMLDivElement | null = null;
let onOrient: (() => void) | null = null;
let mql: MediaQueryList | null = null;
let timer: number | null = null;

function isPhysicalLandscape(): boolean {
  return window.innerWidth > window.innerHeight;
}

/** Mount the hint (idempotent). Hides itself right away if the device already reports landscape. */
export function showRotateHint(): void {
  if (el) return;
  if (isPhysicalLandscape()) return; // already landscape — nothing to prompt

  const d = document.createElement('div');
  d.id = 'rotate-hint';
  d.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:60',
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'justify-content:center',
    'gap:20px',
    'background:rgba(2,27,14,0.94)',
    'color:#f3e9d2',
    'font-family:Vazirmatn,Tahoma,system-ui,sans-serif',
    'text-align:center',
    'direction:rtl',
    'cursor:pointer',
    'opacity:1',
    'transition:opacity 240ms ease',
  ].join(';');
  d.innerHTML =
    '<div style="font-size:72px;line-height:1;animation:rotHintSpin 1.8s ease-in-out infinite">⟳</div>' +
    '<div style="font-size:24px;font-weight:700;max-width:80vw">برای بازیِ تخته‌نرد، گوشی را بچرخانید</div>' +
    '<div style="font-size:16px;opacity:0.75">صفحه را افقی بگیرید</div>';
  const kf = document.createElement('style');
  kf.textContent =
    '@keyframes rotHintSpin{0%{transform:rotate(-15deg)}50%{transform:rotate(75deg)}100%{transform:rotate(-15deg)}}';
  d.appendChild(kf);

  d.addEventListener('pointerdown', () => hideRotateHint());
  document.body.appendChild(d);
  el = d;

  onOrient = () => {
    if (isPhysicalLandscape()) hideRotateHint();
  };
  window.addEventListener('resize', onOrient);
  window.addEventListener('orientationchange', onOrient);
  mql = window.matchMedia('(orientation: landscape)');
  mql.addEventListener?.('change', onOrient);

  timer = window.setTimeout(() => hideRotateHint(), AUTO_DISMISS_MS);
}

/** Remove the hint and detach all listeners/timers (idempotent; safe to call when already hidden). */
export function hideRotateHint(): void {
  if (timer !== null) {
    window.clearTimeout(timer);
    timer = null;
  }
  if (onOrient) {
    window.removeEventListener('resize', onOrient);
    window.removeEventListener('orientationchange', onOrient);
    mql?.removeEventListener?.('change', onOrient);
  }
  onOrient = null;
  mql = null;
  if (!el) return;
  const node = el;
  el = null;
  node.style.opacity = '0';
  window.setTimeout(() => node.parentNode?.removeChild(node), 260);
}
